import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { Briefcase, FileText, Receipt, Building } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface GlobalSearchResult {
  type: 'job' | 'rfq' | 'purchase_order' | 'vendor';
  id: string;
  publicId: string;
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  href: string;
  status?: string;
}

interface UseGlobalSearchOptions {
  enabled: boolean;
  debounceMs?: number;
}

interface SearchResults {
  jobs: GlobalSearchResult[];
  rfqs: GlobalSearchResult[];
  purchaseOrders: GlobalSearchResult[];
  vendors: GlobalSearchResult[];
}

export function useGlobalSearch(query: string, options: UseGlobalSearchOptions) {
  const { user, organization } = useAuth();
  const { enabled, debounceMs = 300 } = options;
  
  const [results, setResults] = useState<SearchResults>({
    jobs: [],
    rfqs: [],
    purchaseOrders: [],
    vendors: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Debounced search query
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Perform search when debounced query changes
  useEffect(() => {
    const performSearch = async () => {
      // Skip if not enabled, no query, or query too short
      if (!enabled || !debouncedQuery || debouncedQuery.length < 2) {
        setResults({
          jobs: [],
          rfqs: [],
          purchaseOrders: [],
          vendors: [],
        });
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const searchTerm = `%${debouncedQuery}%`;
        const userRole = user?.role;
        const orgId = organization?.id;

        // Execute searches based on role
        let jobsData: any[] = [];
        let rfqsData: any[] = [];
        let posData: any[] = [];
        let vendorsData: any[] = [];

        // Jobs search
        if (userRole === 'admin') {
          const { data } = await supabase
            .from('jobs')
            .select('id, public_id, title, status, category')
            .eq('is_deleted', false)
            .or(`title.ilike.${searchTerm},description.ilike.${searchTerm},public_id.ilike.${searchTerm}`)
            .order('updated_at', { ascending: false })
            .limit(5);
          jobsData = data || [];
        } else if (userRole === 'client' && orgId) {
          const { data } = await supabase
            .from('jobs')
            .select('id, public_id, title, status, category')
            .eq('organization_id', orgId)
            .eq('is_deleted', false)
            .or(`title.ilike.${searchTerm},description.ilike.${searchTerm},public_id.ilike.${searchTerm}`)
            .order('updated_at', { ascending: false })
            .limit(5);
          jobsData = data || [];
        } else if (userRole === 'vendor' && orgId) {
          // 1. Get jobs from existing purchase orders
          const { data: poData } = await supabase
            .from('purchase_orders')
            .select('job:jobs!inner(id, public_id, title, status, category)')
            .eq('vendor_organization_id', orgId)
            .limit(20);

          // 2. Get jobs where vendor has been awarded (winning bid)
          const { data: awardData } = await supabase
            .from('awards')
            .select(`
              job:jobs!inner(id, public_id, title, status, category),
              winning_bid:bids!inner(
                vendor_user_id,
                vendor_profile:profiles!inner(organization_id)
              )
            `)
            .limit(50);

          // 3. Get jobs where vendor has active bids
          const { data: bidData } = await supabase
            .from('bids')
            .select(`
              vendor_user_id,
              rfq:rfqs!inner(
                job:jobs!inner(id, public_id, title, status, category)
              )
            `)
            .eq('vendor_user_id', user?.id)
            .in('status', ['submitted', 'selected'])
            .limit(20);

          // Combine and deduplicate by job ID
          const jobMap = new Map<string, any>();
          const term = debouncedQuery.toLowerCase();
          
          // Add PO jobs
          (poData || []).forEach((po: any) => {
            if (po.job) jobMap.set(po.job.id, po.job);
          });
          
          // Add awarded jobs (filter by org)
          (awardData || []).forEach((award: any) => {
            if (award.job && award.winning_bid?.vendor_profile?.organization_id === orgId) {
              jobMap.set(award.job.id, award.job);
            }
          });
          
          // Add bid jobs
          (bidData || []).forEach((bid: any) => {
            if (bid.rfq?.job) jobMap.set(bid.rfq.job.id, bid.rfq.job);
          });

          // Filter by search term in memory
          jobsData = Array.from(jobMap.values())
            .filter((job: any) => {
              return job.title?.toLowerCase().includes(term) || 
                     job.public_id?.toLowerCase().includes(term);
            })
            .slice(0, 5);
        }

        // RFQs search - fetch more and filter in memory to search both RFQ public_id and job title
        const rfqSearchTerm = debouncedQuery.toLowerCase();
        if (userRole === 'admin') {
          const { data } = await supabase
            .from('rfqs')
            .select('id, public_id, status, job:jobs!inner(title, public_id)')
            .order('created_at', { ascending: false })
            .limit(20);
          rfqsData = (data || [])
            .filter((rfq: any) => {
              return rfq.public_id?.toLowerCase().includes(rfqSearchTerm) ||
                     rfq.job?.title?.toLowerCase().includes(rfqSearchTerm) ||
                     rfq.job?.public_id?.toLowerCase().includes(rfqSearchTerm);
            })
            .slice(0, 5);
        } else if (userRole === 'client' && orgId) {
          const { data } = await supabase
            .from('rfqs')
            .select('id, public_id, status, job:jobs!inner(title, public_id, organization_id)')
            .order('created_at', { ascending: false })
            .limit(50);
          // Filter for org AND search term in memory
          rfqsData = (data || [])
            .filter((rfq: any) => {
              const isOrgMatch = rfq.job?.organization_id === orgId;
              const isSearchMatch = rfq.public_id?.toLowerCase().includes(rfqSearchTerm) ||
                                   rfq.job?.title?.toLowerCase().includes(rfqSearchTerm) ||
                                   rfq.job?.public_id?.toLowerCase().includes(rfqSearchTerm);
              return isOrgMatch && isSearchMatch;
            })
            .slice(0, 5);
        } else if (userRole === 'vendor') {
          const { data } = await supabase
            .from('rfqs')
            .select('id, public_id, status, job:jobs!inner(title, public_id)')
            .in('status', ['open', 'pending'])
            .order('created_at', { ascending: false })
            .limit(20);
          rfqsData = (data || [])
            .filter((rfq: any) => {
              return rfq.public_id?.toLowerCase().includes(rfqSearchTerm) ||
                     rfq.job?.title?.toLowerCase().includes(rfqSearchTerm) ||
                     rfq.job?.public_id?.toLowerCase().includes(rfqSearchTerm);
            })
            .slice(0, 5);
        }

        // Purchase Orders search
        if (userRole === 'admin') {
          const { data } = await supabase
            .from('purchase_orders')
            .select('id, public_id, po_number, status, job:jobs!inner(title)')
            .or(`po_number.ilike.${searchTerm},public_id.ilike.${searchTerm}`)
            .order('created_at', { ascending: false })
            .limit(5);
          posData = data || [];
        } else if (userRole === 'client' && orgId) {
          const { data } = await supabase
            .from('purchase_orders')
            .select('id, public_id, po_number, status, job:jobs!inner(title)')
            .eq('organization_id', orgId)
            .or(`po_number.ilike.${searchTerm},public_id.ilike.${searchTerm}`)
            .order('created_at', { ascending: false })
            .limit(5);
          posData = data || [];
        } else if (userRole === 'vendor' && orgId) {
          const { data } = await supabase
            .from('purchase_orders')
            .select('id, public_id, po_number, status, job:jobs!inner(title)')
            .eq('vendor_organization_id', orgId)
            .or(`po_number.ilike.${searchTerm},public_id.ilike.${searchTerm}`)
            .order('created_at', { ascending: false })
            .limit(5);
          posData = data || [];
        }

        // Vendors search (only for clients and admins)
        if (userRole === 'admin' || userRole === 'client') {
          const { data } = await supabase
            .from('organizations')
            .select('id, name')
            .ilike('name', searchTerm)
            .order('name')
            .limit(5);
          vendorsData = data || [];
        }

        // Transform results
        const jobResults: GlobalSearchResult[] = jobsData.map((job: any) => ({
          type: 'job' as const,
          id: job.id,
          publicId: job.public_id,
          title: job.title,
          subtitle: job.category,
          icon: Briefcase,
          href: `/jobs/${job.public_id}`,
          status: job.status,
        }));

        const rfqResults: GlobalSearchResult[] = rfqsData.map((rfq: any) => ({
          type: 'rfq' as const,
          id: rfq.id,
          publicId: rfq.public_id,
          title: rfq.job?.title || `RFQ ${rfq.public_id}`,
          subtitle: `RFQ ${rfq.public_id}`,
          icon: FileText,
          href: `/rfqs/${rfq.public_id}`,
          status: rfq.status,
        }));

        const poResults: GlobalSearchResult[] = posData.map((po: any) => ({
          type: 'purchase_order' as const,
          id: po.id,
          publicId: po.public_id,
          title: po.po_number,
          subtitle: po.job?.title,
          icon: Receipt,
          href: `/purchase-orders/${po.public_id}`,
          status: po.status,
        }));

        const vendorResults: GlobalSearchResult[] = vendorsData.map((org: any) => ({
          type: 'vendor' as const,
          id: org.id,
          publicId: org.id,
          title: org.name,
          icon: Building,
          href: `/vendors/${org.id}`,
        }));

        setResults({
          jobs: jobResults,
          rfqs: rfqResults,
          purchaseOrders: poResults,
          vendors: vendorResults,
        });
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Search failed'));
        console.error('Global search error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, enabled, user?.role, organization?.id]);

  // Compute total count
  const totalCount = useMemo(() => {
    return results.jobs.length + results.rfqs.length + results.purchaseOrders.length + results.vendors.length;
  }, [results]);

  // Flatten results for easier iteration
  const allResults = useMemo(() => {
    return [
      ...results.jobs,
      ...results.rfqs,
      ...results.purchaseOrders,
      ...results.vendors,
    ];
  }, [results]);

  return {
    results,
    allResults,
    totalCount,
    isLoading,
    error,
    hasResults: totalCount > 0,
  };
}
